import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Package,
  MapPin,
  Phone,
  Clock,
  Truck,
  DollarSign,
  Play,
} from 'lucide-react';
import { getOrder, markOrderReady, Order } from '../services/api';
import { format } from 'date-fns';

function StatusBadge({ status }: { status: string }) {
  const badgeClass = {
    PENDING: 'badge-pending',
    READY: 'badge-ready',
    QUOTE_SENT: 'badge-ready',
    QUOTE_APPROVED: 'badge-in-delivery',
    IN_DELIVERY: 'badge-in-delivery',
    DELIVERED: 'badge-delivered',
    CANCELLED: 'badge-cancelled',
    FAILED: 'badge-failed',
  }[status] || 'badge-pending';

  return <span className={`badge ${badgeClass}`}>{status.replace(/_/g, ' ')}</span>;
}

function DeliveryTimeline({ order }: { order: Order }) {
  const delivery = order.deliveries?.[0];
  if (!delivery) return null;

  const steps = [
    { label: 'Quote Approved', completed: true },
    {
      label: 'Driver Assigned',
      completed: ['DRIVER_ASSIGNED', 'EN_ROUTE_TO_PICKUP', 'ARRIVED_AT_PICKUP', 'PICKED_UP', 'EN_ROUTE_TO_DROPOFF', 'ARRIVED_AT_DROPOFF', 'DELIVERED'].includes(delivery.status),
    },
    {
      label: 'Picked Up',
      completed: ['PICKED_UP', 'EN_ROUTE_TO_DROPOFF', 'ARRIVED_AT_DROPOFF', 'DELIVERED'].includes(delivery.status),
      time: delivery.pickedUpAt,
    },
    {
      label: 'Delivered',
      completed: delivery.status === 'DELIVERED',
      time: delivery.deliveredAt,
    },
  ];

  return (
    <div className="card">
      <h3 className="font-semibold mb-4">Delivery Progress</h3>
      <div className="space-y-4">
        {steps.map((step, index) => (
          <div key={index} className="flex items-start gap-3">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center ${
                step.completed ? 'bg-uber-green text-white' : 'bg-gray-200'
              }`}
            >
              {step.completed && (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
            <div>
              <p className={step.completed ? 'font-medium' : 'text-gray-500'}>
                {step.label}
              </p>
              {step.time && (
                <p className="text-sm text-gray-500">
                  {format(new Date(step.time), 'MMM d, h:mm a')}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => getOrder(id!),
    enabled: !!id,
  });

  const markReadyMutation = useMutation({
    mutationFn: markOrderReady,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', id] });
    },
  });

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uber-green"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-8">
        <p>Order not found</p>
      </div>
    );
  }

  const latestQuote = order.quotes?.[0];
  const latestDelivery = order.deliveries?.[0];

  return (
    <div className="p-8">
      <Link
        to="/orders"
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Orders
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">{order.shopifyOrderNumber}</h2>
          <p className="text-gray-500">
            Created {format(new Date(order.createdAt), 'MMM d, yyyy h:mm a')}
          </p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <div className="card">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Customer Information
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium">{order.customerName}</p>
                  <p className="text-gray-600">{order.deliveryAddress}</p>
                  <p className="text-gray-600">
                    {order.deliveryCity}, {order.deliveryState} {order.deliveryZip}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <p>{order.customerPhone}</p>
              </div>
            </div>
          </div>

          {/* Quote Info */}
          {latestQuote && (
            <div className="card">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Quote Details
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Delivery Fee</span>
                  <span className="font-medium">
                    ${(latestQuote.priceAmount / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <StatusBadge status={latestQuote.status} />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expires</span>
                  <span>
                    {format(new Date(latestQuote.expiresAt), 'MMM d, h:mm a')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Driver Info */}
          {latestDelivery && latestDelivery.driverName && (
            <div className="card">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Driver Information
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name</span>
                  <span className="font-medium">{latestDelivery.driverName}</span>
                </div>
                {latestDelivery.driverPhone && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone</span>
                    <span>{latestDelivery.driverPhone}</span>
                  </div>
                )}
                {latestDelivery.etaMinutes && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">ETA</span>
                    <span>{latestDelivery.etaMinutes} minutes</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Actions */}
          {order.status === 'PENDING' && (
            <div className="card">
              <h3 className="font-semibold mb-4">Actions</h3>
              <button
                onClick={() => markReadyMutation.mutate(order.id)}
                disabled={markReadyMutation.isPending}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <Play className="w-4 h-4" />
                {markReadyMutation.isPending ? 'Getting Quote...' : 'Mark Ready & Get Quote'}
              </button>
            </div>
          )}

          {/* Delivery Timeline */}
          {order.status === 'IN_DELIVERY' || order.status === 'DELIVERED' ? (
            <DeliveryTimeline order={order} />
          ) : null}

          {/* Status Timeline */}
          <div className="card">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Timeline
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Created</span>
                <span>{format(new Date(order.createdAt), 'MMM d, h:mm a')}</span>
              </div>
              {order.markedReadyAt && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Marked Ready</span>
                  <span>
                    {format(new Date(order.markedReadyAt), 'MMM d, h:mm a')}
                  </span>
                </div>
              )}
              {latestDelivery?.pickedUpAt && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Picked Up</span>
                  <span>
                    {format(new Date(latestDelivery.pickedUpAt), 'MMM d, h:mm a')}
                  </span>
                </div>
              )}
              {latestDelivery?.deliveredAt && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Delivered</span>
                  <span>
                    {format(new Date(latestDelivery.deliveredAt), 'MMM d, h:mm a')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
